import { html } from '@codemirror/lang-html'
import { oneDark } from '@codemirror/theme-one-dark'
import CodeMirror from '@uiw/react-codemirror'
import { Image } from 'lucide-react'
import type { WpArticle } from '@/api/hooks/use-wordpress'
import { useWpGroupDetail, useWpGroups } from '@/api/hooks/use-wordpress'
import { StatusBadge } from '@/components/ui/status-badge'

interface Props {
  contentType: 'articles' | 'html'
  selectedGroupId: string
  onGroupIdChange: (v: string) => void
  htmlContent: string
  onHtmlContentChange: (v: string) => void
  onBack: () => void
  onNext: () => void
}

export function CampaignStepContent({
  contentType,
  selectedGroupId,
  onGroupIdChange,
  htmlContent,
  onHtmlContentChange,
  onBack,
  onNext,
}: Props): React.JSX.Element {
  const { data: groupsData } = useWpGroups()
  const groups = groupsData?.data ?? []
  const { data: groupDetail } = useWpGroupDetail(selectedGroupId)
  const articles = groupDetail?.articles ?? []

  const canProceed =
    contentType === 'articles'
      ? selectedGroupId && articles.length > 0
      : htmlContent.trim().length > 0

  const nextLabel = contentType === 'articles' ? 'Next: Choose Layout →' : 'Next: Review →'

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      {contentType === 'articles' ? (
        <ArticleGroupPicker
          groups={groups}
          selectedGroupId={selectedGroupId}
          onGroupIdChange={onGroupIdChange}
          articles={articles}
        />
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-text-secondary">Write or paste your email HTML.</p>
          <div className="overflow-hidden rounded-lg border border-surface-3">
            <CodeMirror
              value={htmlContent}
              onChange={onHtmlContentChange}
              extensions={[html()]}
              theme={oneDark}
              height="400px"
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                autocompletion: true,
                bracketMatching: true,
              }}
            />
          </div>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-surface-3 px-5 py-2.5 text-sm font-medium text-text-primary hover:bg-surface-1"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-40"
        >
          {nextLabel}
        </button>
      </div>
    </div>
  )
}

function ArticleGroupPicker({
  groups,
  selectedGroupId,
  onGroupIdChange,
  articles,
}: {
  groups: Array<{ id: string; name: string; article_count: number }>
  selectedGroupId: string
  onGroupIdChange: (v: string) => void
  articles: WpArticle[]
}): React.JSX.Element {
  if (groups.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-surface-3 py-10 text-center">
        <p className="text-sm text-text-tertiary">No article groups yet.</p>
        <p className="mt-1 text-xs text-text-secondary">
          Go to{' '}
          <a href="/wordpress" className="text-brand-500 underline">
            WordPress
          </a>{' '}
          to sync articles and create a group.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="grp-select" className="block text-sm font-medium text-text-primary">
          Article group
        </label>
        <select
          id="grp-select"
          value={selectedGroupId}
          onChange={(e) => onGroupIdChange(e.target.value)}
          className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2.5 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
        >
          <option value="">Select a group...</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name} ({g.article_count} articles)
            </option>
          ))}
        </select>
      </div>

      {articles.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-text-secondary">
            {articles.length} articles in this group:
          </p>
          {articles.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded-lg bg-surface-1 p-2.5">
              {a.cover_image_url ? (
                <img
                  src={a.cover_image_url}
                  alt=""
                  className="h-10 w-14 shrink-0 rounded object-cover"
                />
              ) : (
                <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded bg-surface-2">
                  <Image className="h-4 w-4 text-text-tertiary" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-text-primary">{a.title}</p>
                <p className="text-xs text-text-secondary">{a.author ?? 'Unknown'}</p>
              </div>
              <StatusBadge status={a.status === 'published' ? 'active' : 'pending'}>
                {a.status}
              </StatusBadge>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
